/**
 * Client-side type hub. Everything the wire defines comes from `@spades/shared` (views, cues,
 * protocol, rules); only the CLIENT-ONLY shapes live here:
 *   - `TableView`: the plain mirror of the room's `TableSchema` (`state.toJSON()`), which shared
 *     deliberately does not define (DESIGN-server §3.1 is server-private).
 *   - `Player` / `Participant`: identity and roster shapes for the store.
 */
import {
  HOUSE_RULES,
  type Bid,
  type Controller,
  type Cue,
  type CueType,
  type IntentInputs,
  type Phase,
  type Seat,
  type SeatView,
} from '@spades/shared';

export type {
  AnyCue,
  Bid,
  CardId,
  Controller,
  Cue,
  CueData,
  CueType,
  CuesMessage,
  ErrorMessage,
  Phase,
  PrivateMessage,
  ProgressMessage,
  PublicSeat,
  PublicView,
  Rules,
  RulesInput,
  Seat,
  SeatView,
  Surface,
  TableOptions,
  Team,
} from '@spades/shared';
export { NO_CARD, cueTypes, isCueType } from '@spades/shared';

/** Alias kept for the store/bridge code that predates shared. */
export type SeatIndex = Seat;

/**
 * What the client SENDS (before zod defaults): `setRules.rules` may be partial. The server-side
 * parsed shape is shared `Intents`.
 */
export type Intents = IntentInputs;
export type IntentName = keyof Intents;

/** Plain mirror of `SeatSchema` (DESIGN-server §3.1). */
export interface SeatMirror {
  playerId: string;
  name: string;
  avatar: string;
  controller: Controller;
  /**
   * A bot is playing this seat's turns while its player is still at the table.
   *
   * `controller` stays `'human'` for as long as a human holds the seat, so **never render a bot
   * label from `autopilot`** — the player is right there, and being called a bot on your own seat
   * is what a playtester reported. "Playing for you" is the idea; the label belongs to
   * `controller === 'bot'`, which means nobody is behind the seat at all.
   */
  autopilot: boolean;
  connected: boolean;
  handCount: number;
  bidLocked: boolean;
  /** -1 hidden, 0..13 tricks, 20 nil, 21 blind nil. */
  bid: number;
  hasLockedCard: boolean;
  sealed: boolean;
  blindEligible: boolean;
  tricksTaken: number;
  timeBanksLeft: number;
  consecutiveTimeouts: number;
  /**
   * Lifetime progression level of whoever holds this seat; `0` means "no badge".
   *
   * Live only — the server writes it when a seat is claimed and again when a match ends, and it is
   * the *only* progression number on the schema. XP travels separately, in the one-shot `progress`
   * message, so a reload can never resurrect a frozen mid-match total (plan §3 `rooms/schema.ts`).
   *
   * 0 rather than null because the schema field is a `uint16`: a player with no finished match has
   * no level yet, and every surface that draws the badge treats falsy as "draw nothing".
   */
  level: number;
}

export interface TeamMirror {
  score: number;
  bags: number;
  /** -1 until bids are revealed. */
  contract: number;
}

export interface PlayedMirror {
  seat: number;
  card: number;
}

export interface TimerMirror {
  kind: 'none' | 'bid' | 'play' | 'hold' | 'scored' | string;
  seat: number;
  /** Server time. Compare against `serverNow()` from `net/wire`, never `Date.now()`. */
  endsAt: number;
  /** Server time the window opened; 0 when no timer is running. */
  startedAt: number;
  /** Length of the window in ms. Draw progress as `(serverNow() - startedAt) / durationMs`. */
  durationMs: number;
  forced: boolean;
}

/** Authoritative public table state: `TableSchema.toJSON()` (DESIGN-client §1.1 `table`). */
export interface TableView {
  code: string;
  surface: 'discord' | 'web';
  phase: Phase | string;
  rulesJson: string;
  handNo: number;
  dealerSeat: number;
  leader: number;
  turn: number;
  trickNo: number;
  spadesBroken: boolean;
  suddenDeath: number;
  seats: SeatMirror[];
  teams: TeamMirror[];
  trick: PlayedMirror[];
  timer: TimerMirror;
  cueSeq: number;
  /** The server's `Date.now()` as of the last timer arm; the driver samples it for the offset. */
  serverTime: number;
  spectators: number;
  hostPlayerId: string;
  rematchVotes: Record<string, string>;
  /**
   * Players who asked to sit while the table was mid-hand, keyed by playerId. The value is the
   * seat they asked for, or `ANY_SEAT` (255) for "any". They are seated at the next hand boundary,
   * so a lobby can render "Seated next hand" from this.
   */
  pendingSeats: Record<string, number>;
  winner: number;
}

/** `pendingSeats` value meaning the player did not name a seat. */
export const ANY_SEAT = 255;

/** Schema `bid` int8 → shared `Bid | null` (null = hidden). */
export function bidFromSchema(b: number): Bid | null {
  if (b < 0) return null;
  if (b === 20) return { kind: 'nil' };
  if (b === 21) return { kind: 'blindNil' };
  return { kind: 'tricks', n: b };
}

/** Shared `Bid` → schema int8. */
export function bidToSchema(b: Bid | null): number {
  if (!b) return -1;
  if (b.kind === 'nil') return 20;
  if (b.kind === 'blindNil') return 21;
  return b.n;
}

/**
 * The identity the server issued (JWT claims subset; DESIGN-server §6.2). `email` is a web guest
 * who saved their progress with a magic link (docs/accounts/PLAN.md); the address itself never
 * reaches the client.
 */
export interface Player {
  id: string;
  name: string;
  avatar: string | null;
  kind: 'discord' | 'guest' | 'email';
}

/** Someone in the Activity instance / lobby (bridge participants). */
export interface Participant {
  id: string;
  username: string;
  displayName: string;
  avatarUrl: string | null;
}

/** A cue as a script may spell it: `seq`/`t` optional, `data` unchecked JSON. */
export interface LooseCue {
  type: CueType;
  seq?: number;
  t?: number;
  data?: unknown;
}

export function toCue(loose: LooseCue, seq: number, t: number = Date.now()): Cue {
  return { seq: loose.seq ?? seq, t: loose.t ?? t, type: loose.type, data: loose.data ?? {} } as Cue;
}

export function emptySeatMirror(): SeatMirror {
  return {
    playerId: '',
    name: '',
    avatar: '',
    controller: 'empty',
    connected: false,
    handCount: 0,
    bidLocked: false,
    bid: -1,
    hasLockedCard: false,
    sealed: false,
    blindEligible: false,
    tricksTaken: 0,
    timeBanksLeft: 2,
    consecutiveTimeouts: 0,
    autopilot: false,
    level: 0,
  };
}

/**
 * The code reported for a transport-level error with no websocket close code of its own.
 *
 * `onError` carries two different things: the room's own `error {code,msg,intent}` messages, whose
 * codes are always names (`not_your_turn`, `bad_phase`), and the SDK's socket errors, whose codes
 * are numeric — or absent. Callers tell them apart by that: `wire.ts` decides whether a refusal is
 * something the scene can repair, and the e2e puppet decides whether it is a client defect or a
 * network that hiccuped.
 *
 * It lives here rather than in `ColyseusDriver` so `wire.ts` can read it without importing the
 * Colyseus SDK — `wire` is pulled in by the HUD and the seat layer, which must stay off the
 * first-interactive path's dependency on the driver chunk.
 */
export const SOCKET_ERROR_CODE = 'socket';

export function emptyTableView(): TableView {
  return {
    code: '',
    surface: 'web',
    phase: 'lobby',
    rulesJson: '',
    handNo: 0,
    dealerSeat: -1,
    leader: -1,
    turn: -1,
    trickNo: 0,
    spadesBroken: false,
    suddenDeath: 0,
    seats: Array.from({ length: 4 }, () => emptySeatMirror()),
    teams: [
      { score: 0, bags: 0, contract: -1 },
      { score: 0, bags: 0, contract: -1 },
    ],
    trick: [],
    timer: { kind: 'none', seat: -1, endsAt: 0, startedAt: 0, durationMs: 0, forced: false },
    cueSeq: 0,
    serverTime: 0,
    spectators: 0,
    hostPlayerId: '',
    rematchVotes: {},
    pendingSeats: {},
    winner: -1,
  };
}

/** A complete, empty private view (scripts patch over this). */
export function emptySeatView(me: Seat = 0): SeatView {
  const seat = () => ({
    controller: 'empty' as Controller,
    playerId: null,
    handCount: 0,
    bidLocked: false,
    bid: null,
    hasLockedCard: false,
    sealed: false,
    blindEligible: false,
    tricksTaken: 0,
    timeBanksLeft: 2,
    consecutiveTimeouts: 0,
    autopilot: false,
  });
  const row = (): [boolean, boolean, boolean, boolean] => [false, false, false, false];
  return {
    phase: 'lobby',
    rules: HOUSE_RULES,
    handNo: 0,
    dealerSeat: null,
    leader: null,
    turn: null,
    trick: [],
    trickNo: 0,
    spadesBroken: false,
    seats: [seat(), seat(), seat(), seat()],
    teams: [
      { score: 0, bags: 0 },
      { score: 0, bags: 0 },
    ],
    lastTrick: null,
    history: [],
    suddenDeath: 0,
    winner: null,
    me,
    hand: [],
    myBid: null,
    lockedCard: null,
    legal: [],
    legalBids: [],
    cardsPlayed: [],
    voids: [row(), row(), row(), row()],
  };
}
